import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import ModalVideo from "react-modal-video";
import T from "./Translate/T";

const ProjectDetailsModal = ({ projectDetails, setIsOpen }) => {
  const sliderRef = useRef();
  const [activeGroup, setActiveGroup] = useState(0);

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
    CASESTUDY: "casestudy",
  };

  const SlickArrowLeft = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-prev slick-arrow" + (currentSlide === 0 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === 0}
      type="button"
    >
      <i className="fa fa-chevron-left"></i>
    </button>
  );

  const SlickArrowRight = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-next slick-arrow" +
        (currentSlide === slideCount - 1 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === slideCount - 1}
      type="button"
    >
      <i className="fa fa-chevron-right"></i>
    </button>
  );

  const sliderSettings = {
    dots: true,
    arrows: true,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,
    infinite: true,
    adaptiveHeight: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 4500,
  };

  useEffect(() => {
    if (projectDetails?.type === types.DOCUMENT && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [projectDetails, types.DOCUMENT]);

  // Reset active group when modal opens
  useEffect(() => {
    setActiveGroup(0);
  }, [projectDetails]);

  /* ─── Styles scoped to the case study modal — uses CSS vars for dark mode ─── */
  const cs = {
    sectionTitle: {
      fontSize: "11px",
      fontWeight: "700",
      letterSpacing: "2px",
      textTransform: "uppercase",
      color: "var(--color-text-muted, #888)",
      marginBottom: "12px",
      marginTop: "32px",
    },
    metaRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "24px",
      marginBottom: "24px",
      padding: "16px 20px",
      background: "var(--color-surface, #f8f8f8)",
      borderLeft: "3px solid var(--color-heading, #000)",
    },
    metaItem: {
      minWidth: "160px",
    },
    metaLabel: {
      fontSize: "10px",
      fontWeight: "700",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      color: "var(--color-text-muted, #aaa)",
      marginBottom: "2px",
    },
    metaValue: {
      fontSize: "13px",
      fontWeight: "600",
      color: "var(--color-heading, #111)",
    },
    phaseCard: {
      display: "flex",
      gap: "16px",
      padding: "18px 20px",
      marginBottom: "12px",
      background: "var(--color-card-bg, #fff)",
      border: "1px solid var(--color-card-border, #e8e8e8)",
      borderRadius: "2px",
    },
    phaseNumber: {
      fontSize: "22px",
      fontWeight: "800",
      color: "var(--color-border, #e0e0e0)",
      lineHeight: "1",
      minWidth: "32px",
    },
    phaseIcon: {
      width: "36px",
      height: "36px",
      background: "var(--color-heading, #000)",
      color: "var(--color-bg, #fff)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      flexShrink: 0,
    },
    phaseTitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "var(--color-heading, #111)",
      marginBottom: "6px",
    },
    phaseDesc: {
      fontSize: "13px",
      color: "var(--color-text, #555)",
      lineHeight: "1.6",
      margin: 0,
    },
    entityRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      padding: "8px 0",
      borderBottom: "1px solid var(--color-border, #f0f0f0)",
    },
    entityName: {
      fontSize: "13px",
      fontWeight: "700",
      color: "var(--color-heading, #111)",
      minWidth: "120px",
    },
    entityNote: {
      fontSize: "12px",
      color: "var(--color-text, #666)",
    },
    deliverableItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "6px 0",
      fontSize: "13px",
      color: "var(--color-text, #333)",
    },
    checkIcon: {
      width: "18px",
      height: "18px",
      background: "var(--color-primary, #f5df4e)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "var(--color-primary-text, #000)",
      fontSize: "9px",
    },
    valueCard: {
      padding: "18px 20px",
      background: "var(--color-surface, #f8f8f8)",
      borderTop: "3px solid var(--color-heading, #000)",
      marginBottom: "12px",
    },
    valueTitle: {
      fontSize: "13px",
      fontWeight: "700",
      color: "var(--color-heading, #111)",
      marginBottom: "6px",
    },
    valueBody: {
      fontSize: "12px",
      color: "var(--color-text, #555)",
      lineHeight: "1.6",
      margin: 0,
    },
    divider: {
      height: "1px",
      background: "var(--color-border, #e8e8e8)",
      margin: "28px 0",
    },
    // Slideshow group styles
    groupTab: {
      padding: "10px 16px",
      fontSize: "12px",
      fontWeight: "600",
      border: "1px solid var(--color-card-border, #e0e0e0)",
      background: "var(--color-card-bg, #fff)",
      color: "var(--color-text, #333)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderRadius: "4px",
      marginRight: "8px",
      marginBottom: "8px",
    },
    groupTabActive: {
      background: "var(--color-heading, #000)",
      color: "var(--color-bg, #fff)",
      borderColor: "var(--color-heading, #000)",
    },
    slideshowContainer: {
      background: "var(--color-surface, #f5f5f5)",
      padding: "20px",
      borderRadius: "4px",
      marginTop: "16px",
    },
    groupDescription: {
      fontSize: "13px",
      color: "var(--color-text-muted, #666)",
      marginBottom: "16px",
      fontStyle: "italic",
    },
    // Technical Overview styles
    techCard: {
      background: "var(--color-card-bg, #fff)",
      border: "1px solid var(--color-card-border, #e8e8e8)",
      borderRadius: "4px",
      padding: "16px",
      marginBottom: "16px",
    },
    techCardTitle: {
      fontSize: "11px",
      fontWeight: "700",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      color: "var(--color-text-muted, #888)",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    techTable: {
      width: "100%",
      fontSize: "12px",
    },
    techRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid var(--color-border, #f0f0f0)",
    },
    techLabel: {
      color: "var(--color-text-muted, #666)",
    },
    techValue: {
      fontWeight: "600",
      color: "var(--color-heading, #111)",
    },
    featureItem: {
      padding: "8px 0",
      borderBottom: "1px solid var(--color-border, #f0f0f0)",
    },
    featureName: {
      fontSize: "12px",
      fontWeight: "600",
      color: "var(--color-heading, #111)",
    },
    featureDesc: {
      fontSize: "11px",
      color: "var(--color-text-muted, #666)",
      marginTop: "2px",
    },
    liveUrlBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 16px",
      background: "var(--color-heading, #000)",
      color: "var(--color-bg, #fff)",
      fontSize: "12px",
      fontWeight: "600",
      textDecoration: "none",
      borderRadius: "4px",
      marginRight: "8px",
      marginBottom: "8px",
    },
  };

  // Check if this is Muisca (has technicalOverview)
  const isMuisca = projectDetails?.caseStudy?.technicalOverview;

  return (
    <>
      {/* ═══════════════════════════════════════════
          CASE STUDY MODAL
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.CASESTUDY && (
        <div className="project-details-modal">
          <div
            className="modal fade bg-dark-1 show"
            style={{ display: "block", overflow: "auto" }}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="modal-dialog modal-xl"
              style={{ maxWidth: "1100px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content rounded-0">
                <div className="modal-body" style={{ padding: "40px" }}>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsOpen(false)}
                  />

                  {/* Header */}
                  <div style={{ marginBottom: "8px" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                        color: "var(--color-text-muted, #888)",
                      }}
                    >
                      <T>Case Study</T>
                    </span>
                  </div>
                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      marginBottom: "24px",
                      color: "var(--color-heading, #111)",
                    }}
                  >
                    <T>{projectDetails.title}</T>
                  </h2>

                  {/* Live URL Buttons (for Muisca) */}
                  {projectDetails.caseStudy.liveUrl && (
                    <div style={{ marginBottom: "24px" }}>
                      <a
                        href={projectDetails.caseStudy.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={cs.liveUrlBtn}
                      >
                        <i className="fas fa-external-link-alt"></i>
                        <T>View Live Site</T>
                      </a>
                    </div>
                  )}

                  {/* Meta Row */}
                  <div style={cs.metaRow}>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}><T>Client</T></div>
                      <div style={cs.metaValue}><T>{projectDetails.caseStudy.client}</T></div>
                    </div>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}><T>Role</T></div>
                      <div style={cs.metaValue}><T>{projectDetails.caseStudy.role}</T></div>
                    </div>
                    <div style={cs.metaItem}>
                      <div style={cs.metaLabel}><T>Date</T></div>
                      <div style={cs.metaValue}><T>{projectDetails.caseStudy.date}</T></div>
                    </div>
                    <div style={{ ...cs.metaItem, minWidth: "240px" }}>
                      <div style={cs.metaLabel}><T>Stack</T></div>
                      <div style={cs.metaValue}>{projectDetails.caseStudy.technologies}</div>
                    </div>
                  </div>

                  {/* Brief */}
                  <p style={cs.sectionTitle}><T>The Brief</T></p>
                  <p style={{ fontSize: "14px", color: "var(--color-text, #444)", lineHeight: "1.7" }}>
                    <T>{projectDetails.caseStudy.brief}</T>
                  </p>

                  {/* Goal */}
                  <p style={cs.sectionTitle}><T>Goal & Approach</T></p>
                  <p style={{ fontSize: "14px", color: "var(--color-text, #444)", lineHeight: "1.7" }}>
                    <T>{projectDetails.caseStudy.goal}</T>
                  </p>

                  <div style={cs.divider} />

                  {/* ═══════════════════════════════════════════
                      TECHNICAL OVERVIEW (Muisca Only)
                  ═══════════════════════════════════════════ */}
                  {isMuisca && (
                    <>
                      <p style={cs.sectionTitle}><T>Technical Overview</T></p>
                      <div className="row g-3">
                        {/* Live URLs */}
                        <div className="col-md-6">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-globe"></i> <T>Live URLs</T>
                            </div>
                            {projectDetails.caseStudy.technicalOverview.liveUrls.map((item, i) => (
                              <div key={i} style={cs.techRow}>
                                <span style={cs.techLabel}><T>{item.service}</T></span>
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...cs.techValue, color: "#0066cc", textDecoration: "none" }}
                                >
                                  {item.url.replace("https://", "")}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Architecture */}
                        <div className="col-md-6">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-layer-group"></i> <T>Architecture</T>
                            </div>
                            {projectDetails.caseStudy.technicalOverview.architecture.map((item, i) => (
                              <div key={i} style={cs.techRow}>
                                <span style={cs.techLabel}><T>{item.layer}</T></span>
                                <span style={cs.techValue}>{item.technology}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Infrastructure */}
                        <div className="col-md-6">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-server"></i> <T>Infrastructure</T>
                            </div>
                            {projectDetails.caseStudy.technicalOverview.infrastructure.map((item, i) => (
                              <div key={i} style={cs.techRow}>
                                <span style={cs.techLabel}><T>{item.component}</T></span>
                                <span style={cs.techValue}>{item.details}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Security */}
                        <div className="col-md-6">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-shield-alt"></i> <T>Security</T>
                            </div>
                            {projectDetails.caseStudy.technicalOverview.security.map((item, i) => (
                              <div key={i} style={cs.techRow}>
                                <span style={cs.techLabel}>{item.feature}</span>
                                <span style={{ ...cs.techValue, color: item.status ? "#28a745" : "#dc3545" }}>
                                  {item.status ? <T>✓ Enabled</T> : <T>✗ Disabled</T>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Key Features */}
                        <div className="col-12">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-star"></i> <T>Key Features</T>
                            </div>
                            <div className="row">
                              {projectDetails.caseStudy.technicalOverview.keyFeatures.map((item, i) => (
                                <div key={i} className="col-md-6">
                                  <div style={cs.featureItem}>
                                    <div style={cs.featureName}><T>{item.name}</T></div>
                                    <div style={cs.featureDesc}><T>{item.desc}</T></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Capacity */}
                        <div className="col-12">
                          <div style={cs.techCard}>
                            <div style={cs.techCardTitle}>
                              <i className="fas fa-tachometer-alt"></i> <T>Capacity & Performance</T>
                            </div>
                            <div className="row">
                              {projectDetails.caseStudy.technicalOverview.capacity.map((item, i) => (
                                <div key={i} className="col-md-3 col-6">
                                  <div style={{ textAlign: "center", padding: "12px" }}>
                                    <div style={{ fontSize: "18px", fontWeight: "700", color: "var(--color-heading, #111)" }}>
                                      <T>{item.value}</T>
                                    </div>
                                    <div style={{ fontSize: "11px", color: "var(--color-text-muted, #666)", marginTop: "4px" }}>
                                      <T>{item.metric}</T>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={cs.divider} />
                    </>
                  )}

                  {/* ═══════════════════════════════════════════
                      DESIGN PROCESS PHASES (Non-Muisca)
                  ═══════════════════════════════════════════ */}
                  {projectDetails.caseStudy.phases && (
                    <>
                      <p style={cs.sectionTitle}><T>Design & Development Process</T></p>
                      {projectDetails.caseStudy.phases.map((phase, i) => (
                        <div key={i} style={cs.phaseCard}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                            <div style={cs.phaseNumber}>{phase.number}</div>
                            <div style={cs.phaseIcon}>
                              <i className={phase.icon}></i>
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={cs.phaseTitle}><T>{phase.title}</T></div>
                            <p style={cs.phaseDesc}><T>{phase.description}</T></p>
                          </div>
                        </div>
                      ))}

                      <div style={cs.divider} />
                    </>
                  )}

                  {/* ═══════════════════════════════════════════
                      SLIDESHOW GROUPS
                  ═══════════════════════════════════════════ */}
                  {projectDetails.caseStudy.slideshowGroups && (
                    <>
                      <p style={cs.sectionTitle}><T>Visual Documentation</T></p>
                      
                      {/* Group Tabs */}
                      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "8px" }}>
                        {projectDetails.caseStudy.slideshowGroups.map((group, i) => (
                          <button
                            key={i}
                            style={{
                              ...cs.groupTab,
                              ...(activeGroup === i ? cs.groupTabActive : {}),
                            }}
                            onClick={() => setActiveGroup(i)}
                          >
                            <T>{group.title}</T>
                            <span style={{ 
                              marginLeft: "6px", 
                              opacity: 0.6,
                              fontSize: "10px" 
                            }}>
                              ({group.images.length})
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Active Slideshow */}
                      <div style={cs.slideshowContainer}>
                        <p style={cs.groupDescription}>
                          <T>{projectDetails.caseStudy.slideshowGroups[activeGroup].description}</T>
                        </p>
                        <Slider {...sliderSettings} key={activeGroup}>
                          {projectDetails.caseStudy.slideshowGroups[activeGroup].images.map((img, i) => (
                            <div key={i}>
                              <img
                                src={img}
                                alt={`${projectDetails.caseStudy.slideshowGroups[activeGroup].title} - ${i + 1}`}
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                }}
                              />
                            </div>
                          ))}
                        </Slider>
                      </div>

                      <div style={cs.divider} />
                    </>
                  )}

                  {/* ═══════════════════════════════════════════
                      ARCHITECTURE (Non-Muisca)
                  ═══════════════════════════════════════════ */}
                  {projectDetails.caseStudy.architecture && !isMuisca && (
                    <div className="row g-4">
                      <div className="col-md-6">
                        <p style={cs.sectionTitle}><T>Architecture</T></p>
                        <div style={{ marginBottom: "16px" }}>
                          <div style={cs.metaLabel}><T>Stack</T></div>
                          <div style={{ fontSize: "13px", color: "var(--color-text, #333)", marginTop: "4px" }}>
                            {projectDetails.caseStudy.architecture.stack}
                          </div>
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                          <div style={cs.metaLabel}><T>Authentication</T></div>
                          <div style={{ fontSize: "13px", color: "var(--color-text, #333)", marginTop: "4px" }}>
                            {projectDetails.caseStudy.architecture.auth}
                          </div>
                        </div>
                        <div>
                          <div style={cs.metaLabel}>
                            <T>Database</T> — {projectDetails.caseStudy.architecture.entities}
                          </div>
                          <div style={{ marginTop: "8px" }}>
                            {projectDetails.caseStudy.architecture.keyEntities.map((entity, i) => (
                              <div key={i} style={cs.entityRow}>
                                <span style={cs.entityName}>{entity.name}</span>
                                <span style={cs.entityNote}><T>{entity.note}</T></span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="col-md-6">
                        <p style={cs.sectionTitle}><T>Deliverables</T></p>
                        {projectDetails.caseStudy.deliverables.map((item, i) => (
                          <div key={i} style={cs.deliverableItem}>
                            <div style={cs.checkIcon}>
                              <i className="fas fa-check"></i>
                            </div>
                            <T>{item}</T>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {projectDetails.caseStudy.architecture && !isMuisca && (
                    <div style={cs.divider} />
                  )}

                  {/* Value Proposition */}
                  <p style={cs.sectionTitle}><T>Value Delivered</T></p>
                  <div className="row g-3">
                    {projectDetails.caseStudy.valueProposition.map((vp, i) => (
                      <div key={i} className="col-md-4">
                        <div style={cs.valueCard}>
                          <div style={cs.valueTitle}><T>{vp.title}</T></div>
                          <p style={cs.valueBody}><T>{vp.body}</T></p>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          DOCUMENT MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.DOCUMENT && (
        <div className="project-details-modal">
          <div
            className="modal fade bg-dark-1 show"
            style={{ display: "block" }}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="modal-dialog modal-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content rounded-0">
                <div className="modal-body">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsOpen(false)}
                  />
                  <div className="container ajax-container">
                    <h2 className="text-6 fw-600 text-center mb-4">
                      {projectDetails?.title}
                    </h2>
                    <div className="row g-4">
                      <div className="col-md-7">
                        <Slider {...sliderSettings} ref={sliderRef}>
                          <div className="item">
                            <img
                              className="img-fluid"
                              alt=""
                              src={projectDetails?.thumbImage}
                            />
                          </div>
                          {projectDetails?.document?.sliderImages?.map(
                            (image, index) => (
                              <div className="item" key={index}>
                                <img className="img-fluid" alt="" src={image} />
                              </div>
                            )
                          )}
                        </Slider>
                      </div>
                      <div className="col-md-5">
                        <h4 className="text-4 fw-600">Project Info:</h4>
                        <p>{projectDetails?.document?.projectInfo}</p>
                        <h4 className="text-4 fw-600 mt-4">Project Details:</h4>
                        <ul className="list-style-2">
                          <li>
                            <span className="text-dark fw-600 me-2">Client:</span>
                            {projectDetails?.document?.client}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Technologies:</span>
                            {projectDetails?.document?.technologies}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Industry:</span>
                            {projectDetails?.document?.industry}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">Date:</span>
                            {projectDetails?.document?.date}
                          </li>
                          <li>
                            <span className="text-dark fw-600 me-2">URL:</span>
                            <a
                              href={projectDetails?.document?.url?.link}
                              className="btn btn-primary shadow-none rounded-0 px-3 py-1"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {projectDetails?.document?.url?.name}
                              <i className="fas fa-external-link-alt ms-1" />
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          IMAGE MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.IMAGE && (
        <div className="portfolio-image-modal" onClick={() => setIsOpen(false)}>
          <img
            src={projectDetails?.thumbImage}
            alt={projectDetails?.title}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="btn-close btn-close-white opacity-10"
            onClick={() => setIsOpen(false)}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════
          VIDEO MODAL (unchanged)
      ═══════════════════════════════════════════ */}
      {projectDetails?.type === types.VIDEO && (
        <ModalVideo
          channel={projectDetails?.video?.vimeo ? "vimeo" : "youtube"}
          autoplay
          isOpen={projectDetails?.type === types.VIDEO}
          videoId={projectDetails?.video?.id}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default ProjectDetailsModal;